
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PERMISSIONS, ROLE_PERMISSIONS } from '@/utils/roleManagement';
import { useAuth } from '@/context/SupabaseAuthContext';
import { UserRole } from '@/types/userRole';
import RoleBasedAccess from '@/components/auth/RoleBasedAccess';

const RoleManagementSection: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role;
  const [roles] = useState<UserRole[]>(['admin', 'manager', 'user', 'business']);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Management</CardTitle>
        <CardDescription>
          Manage user roles and permissions in your organization
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RoleBasedAccess 
          permission={PERMISSIONS.VIEW_USERS}
          userRole={role as UserRole}
          fallback={
            <div className="p-6 text-center">
              <p className="text-gray-500">You do not have permission to view role management.</p>
            </div>
          }
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead className="text-right">
                  <RoleBasedAccess 
                    permission={PERMISSIONS.EDIT_SETTINGS} 
                    userRole={role as UserRole}
                  >
                    Actions
                  </RoleBasedAccess>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((roleName) => (
                <TableRow key={roleName}>
                  <TableCell className="font-medium">
                    <Badge 
                      variant={roleName === 'admin' ? 'default' : roleName === 'manager' ? 'secondary' : 'outline'}
                    >
                      {roleName}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {ROLE_PERMISSIONS[roleName]?.slice(0, 3).map((permission) => (
                        <Badge variant="outline" key={permission}>
                          {permission.split(':')[0]}
                        </Badge>
                      ))}
                      {ROLE_PERMISSIONS[roleName]?.length > 3 && (
                        <Badge variant="outline">+{ROLE_PERMISSIONS[roleName].length - 3} more</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <RoleBasedAccess 
                      permission={PERMISSIONS.EDIT_SETTINGS} 
                      userRole={role as UserRole}
                    >
                      <Button variant="outline" size="sm">Edit</Button>
                    </RoleBasedAccess>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </RoleBasedAccess>
      </CardContent>
    </Card>
  );
};

export default RoleManagementSection;
